import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useAuthState } from "../../provider/AuthProvider";
import type { ColumnsType, SorterResult } from "antd/es/table/interface";
import { type TableProps } from "antd/es/table/InternalTable";
import Space from "antd/es/space/index";
import Tag from "antd/es/tag/index";
import Badge from "antd/es/badge/index";
import { useSelector, useDispatch } from "react-redux";
import { actionSetRunningsByCar } from "../../utils/action";

import * as TypeUtils from "../../commons/typeUtils";
import * as String from "../../commons/string";
import * as Common from "../../commons/common";
import * as TypeDTO from "../../commons/typeDTO";
import * as Utils from "../../utils/utils";
import * as RequestRunning from "../../utils/requestApiRunning";

import Search from "./Search";
import DataTable from "../common/DataTable";
import moment from "moment";

type SearchType = {
    startDate: string;
    endDate: string;
    checkbox: string[];
};

type SearchDateType = {
    startDate: string;
    endDate: string;
};

const TabRuninngs = ({ carId, carNumber }: { carId?: number; carNumber?: string }) => {
    const navigate = useNavigate();
    const userDetails = useAuthState();
    const dispatch = useDispatch();
    const reducerRunningsByCar = useSelector((state) => state.runningsByCar);

    const [runnings, setRunnings] = useState<Array<TypeDTO.RunningHistoryDto>>([]);
    const [filteredRunnings, setFilteredRunnings] = useState<Array<TypeDTO.RunningHistoryDto>>();
    const [sortedInfo, setSortedInfo] = useState<SorterResult<TypeDTO.RunningHistoryDto>>({});
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<SearchType>();
    const [loading, setLoading] = useState(false);
    const [tempHistories, setTempHistories] = useState<Array<TypeDTO.RunningHistoryDto>>();
    const [requestHistoriesPage, setRequestHistoriesPage] = useState(-1);
    const [date, setDate] = useState<SearchDateType>();
    const [pageSize, setPageSize] = useState<number>(20);
    const [enableMoreData, setEnableMoreData] = useState(true);

    const checkCustomer = () => {
        return Utils.roleCheck(userDetails?.user?.authority, [Utils.getAuthority.ROLE_CUSTOMER]);
    };

    const searchByTimeData: TypeUtils.SearchItemType[] = [
        {
            id: "searchDate",
            span: 24,
            title: String.searchDate,
            type: "rangePicker",
            defaultStartDate: moment().add(-1, "days").format(Common.FORMAT_DATE_TIME),
            defaultEndDate: moment().format(Common.FORMAT_DATE_TIME),
            hasTime: true,
            dateFormat: Common.FORMAT_DATE_TIME,
            searchPeriod: {
                amount: 1,
                unit: "days",
                message: "최대 1일 까지만 선택 가능합니다.",
            },
        },
    ];

    const searchData: TypeUtils.SearchItemType[] = [
        {
            id: "searchDate",
            span: checkCustomer() ? 24 : 12,
            title: String.searchDate,
            type: "rangePicker",
            dateFormat: Common.FORMAT_DATE_TIME,
            defaultStartDate: "empty",
            defaultEndDate: "empty",
        },
        {
            id: "checkbox",
            span: 12,
            title: String.alarmStatus,
            type: "checkbox",
            options: [
                { value: "error", label: String.error },
                { value: "warning", label: String.warning },
                { value: "normal", label: String.normal },
            ],
            defaultValue: ["error", "warning", "normal"],
            hide: checkCustomer(),
        },
    ];

    const [search, setSearch] = useState<TypeUtils.SearchItemType[]>();
    const [searchByTime, setSearchByTime] = useState<TypeUtils.SearchItemType[]>();

    const columns: ColumnsType<TypeDTO.RunningHistoryDto> = [
        {
            title: String.runningDate,
            dataIndex: "startTime",
            key: "startTime",
            sorter: (a, b, sortOrder) => Utils.sortDate(a.startTime, b.startTime, sortOrder),
            sortOrder: sortedInfo.columnKey === "time" ? sortedInfo.order : null,
            render: (_, { startTime }) => {
                return moment.utc(startTime, Common.FORMAT_DATE).format(Common.FORMAT_DATE);
            },
        },
        {
            title: String.error + "/" + String.warning,
            key: "alarm",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.errorCount, b.errorCount, sortOrder),
            sortOrder: sortedInfo.columnKey === "errorCount" ? sortedInfo.order : null,
            render: (_, row) =>
                row.errorCount || row.warningCount ? (
                    <Space size={0}>
                        {row.errorCount ? <Tag className="errorTag">{row.errorCount}</Tag> : ""}
                        {row.warningCount ? <Tag className="warningTag">{row.warningCount}</Tag> : ""}
                    </Space>
                ) : (
                    <Tag className="normalTag">{row.errorCount}</Tag>
                ),
        },
        {
            title: String.startTime,
            dataIndex: "startTime",
            key: "startTime",
            align: "center",
            render: (_, { startTime, rtcCheck }) => {
                return rtcCheck === true ? (
                    <Badge style={{ right: "-4px" }} dot color="var(--danger)">
                        <span className="fs-md">{startTime ? moment(startTime).format(Common.FORMAT_TIME) : String.dash}</span>
                    </Badge>
                ) : startTime ? (
                    <span className="fs-md">{moment(startTime).format(Common.FORMAT_TIME)}</span>
                ) : (
                    String.dash
                );
            },
        },
        {
            title: String.endTime,
            dataIndex: "endTime",
            key: "endTime",
            align: "center",
            render: (_, { startTime, endTime, finish }) => {
                const checkDday = moment(endTime).startOf("day").diff(moment(startTime).startOf("day"), "days");
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {checkDday > 0 && (
                            <Tag className="normalTag" style={{ marginRight: 4 }}>
                                D+{checkDday}
                            </Tag>
                        )}
                        {endTime ? (
                            finish === true ? (
                                <span className="fs-md">{endTime ? moment(endTime).format(Common.FORMAT_MOBILE_DATE_TIME) : String.dash}</span>
                            ) : moment(endTime).isSameOrAfter(moment().subtract(10, "m")) ? (
                                <Tag className="pointTag">운행중</Tag>
                            ) : Utils.roleCheck(userDetails?.user?.authority, [Utils.getAuthority.ROLE_ADMIN]) ? (
                                <Badge style={{ right: "-4px" }} dot color="var(--danger)">
                                    <span className="fc-point fs-md">
                                        {endTime ? moment(endTime).format(Common.FORMAT_MOBILE_DATE_TIME) : String.dash}
                                    </span>
                                </Badge>
                            ) : (
                                <span className="fs-md">{endTime ? moment(endTime).format(Common.FORMAT_MOBILE_DATE_TIME) : String.dash}</span>
                            )
                        ) : (
                            <span className="fs-md">{String.dash}</span>
                        )}
                    </div>
                );
            },
        },
        {
            title: String.runningTime,
            dataIndex: "drivingTime",
            key: "drivingTime",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.drivingTime, b.drivingTime, sortOrder),
            sortOrder: sortedInfo.columnKey === "drivingTime" ? sortedInfo.order : null,
            render: (_, { drivingTime }) => {
                return Utils.secToTime(Number(drivingTime));
            },
        },
        {
            title: (
                <>
                    {String.traveledDistance}
                    <span className="fs-xs"> {String.kmDistanceUnit}</span>
                </>
            ),
            dataIndex: "drivingDistance",
            key: "drivingDistance",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.drivingDistance, b.drivingDistance, sortOrder),
            sortOrder: sortedInfo.columnKey === "drivingDistance" ? sortedInfo.order : null,
            render: (_, { drivingDistance }) => Utils.checkNumberData(drivingDistance),
        },
        {
            title: (
                <>
                    {String.avgSpeed}
                    <span className="fs-xs"> {String.kmSpeedUnit}</span>
                </>
            ),
            dataIndex: "avgSpeed",
            key: "avgSpeed",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.avgSpeed, b.avgSpeed, sortOrder),
            sortOrder: sortedInfo.columnKey === "avgSpeed" ? sortedInfo.order : null,
            render: (_, { avgSpeed }) => Utils.checkNumberData(avgSpeed),
        },
        {
            title: (
                <>
                    {String.electricMileage}
                    <span className="fs-xs"> {String.kmElectricMileageUnit}</span>
                </>
            ),
            dataIndex: "electricMileage",
            key: "electricMileage",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.electricMileage, b.electricMileage, sortOrder),
            sortOrder: sortedInfo.columnKey === "electricMileage" ? sortedInfo.order : null,
            render: (_, { electricMileage }) => Utils.checkNumberData(electricMileage),
        },
    ].filter((item, index) => item.title !== String.error + "/" + String.warning || !checkCustomer());

    useEffect(() => {
        console.log("reducerRunningsByCar", reducerRunningsByCar);
        if (Object.keys(reducerRunningsByCar).length > 0) {
            setCurrentPage(reducerRunningsByCar?.currentPage);

            setSearch(
                searchData?.map((item) => {
                    const { id, defaultStartDate, defaultEndDate, defaultValue } = item;
                    const isSearchDate = id === "searchDate";

                    if (isSearchDate) {
                        return {
                            ...item,
                            defaultStartDate: reducerRunningsByCar["startDate"] ?? defaultStartDate,
                            defaultEndDate: reducerRunningsByCar["endDate"] ?? defaultEndDate,
                        };
                    } else {
                        return {
                            ...item,
                            defaultValue: reducerRunningsByCar[id] ?? defaultValue,
                        };
                    }
                })
            );

            setSearchByTime(
                searchByTimeData?.map((item) => ({
                    ...item,
                    defaultStartDate: reducerRunningsByCar?.searchPeriodDate?.startDate ?? item.defaultStartDate,
                    defaultEndDate: reducerRunningsByCar?.searchPeriodDate?.endDate ?? item.defaultEndDate,
                }))
            );
        } else {
            setSearch(searchData);
            setSearchByTime(searchByTimeData);
        }
    }, [reducerRunningsByCar]);

    const dispatchRunningsByCar = (selectedId?: number) => {
        dispatch(
            actionSetRunningsByCar({
                ...searchValue,
                searchPeriodDate: date,
                currentPage: currentPage,
                selectedId: selectedId,
                scrollPosition: document.querySelector(".ant-layout-content").scrollTop,
                pageSize: pageSize,
            })
        );
    };

    const onTableChange: TableProps<TypeDTO.RunningHistoryDto>["onChange"] = (pagination, filters, sorter) => {
        setSortedInfo(sorter as SorterResult<TypeDTO.RunningHistoryDto>);
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const onSearchData = (value: any) => {
        const { checkbox, ...searchValue } = value;

        searchValue.carNumber = carNumber;
        if (value?.startDate) searchValue.starDate = moment(value?.startDate).format(Common.FORMAT_DATE_HOUR_TIME_START);
        if (value?.endDate) searchValue.endDate = moment(value?.endDate).format(Common.FORMAT_DATE_HOUR_TIME_END);

        searchValue.error = value?.checkbox?.includes("error");
        searchValue.warning = value?.checkbox?.includes("warning");
        searchValue.normal = value?.checkbox?.includes("normal");

        if (carNumber) {
            setSearchValue(searchValue);
            setLoading(true);

            setRunnings([]);

            if (requestHistoriesPage === 0) {
                requestApiRunningHistoryList(searchValue, requestHistoriesPage);
            } else {
                setRequestHistoriesPage(0);
            }
            // requestApiRunningHistoryList(searchValue, requestHistoriesPage);
        }
    };

    const { requestApiRunningHistoryList, resultApiRunningHistoryList } = RequestRunning.useRequestApiRunningHistoryList();

    useEffect(() => {
        if (resultApiRunningHistoryList === null) return;

        setRunnings((prev) => [...prev, ...resultApiRunningHistoryList.runningHistories.content]);
        setLoading(false);

        if (resultApiRunningHistoryList.runningHistories.totalPages > requestHistoriesPage + 1) {
            setEnableMoreData(true);
        } else {
            setEnableMoreData(false);
        }

        setTimeout(() => {
            document.querySelector(".ant-layout-content").scrollTo({ top: reducerRunningsByCar.scrollPosition });
        }, 100);

        // if (resultApiRunningHistoryList.runningHistories.totalPages > requestHistoriesPage + 1) {
        //     setTempHistories([...(tempHistories || []), ...resultApiRunningHistoryList.runningHistories.content]);
        //     setRequestHistoriesPage(requestHistoriesPage + 1);
        // } else {
        //     setLoading(false);
        //     setRunnings([...(tempHistories || []), ...resultApiRunningHistoryList.runningHistories.content]);
        //     setRequestHistoriesPage(0);
        //     setTempHistories([]);

        //     setTimeout(() => {
        //         document.querySelector(".ant-layout-content").scrollTo({ top: reducerRunningsByCar.scrollPosition });
        //     }, 100);
        // }
    }, [resultApiRunningHistoryList]);

    useEffect(() => {
        if (requestHistoriesPage === -1) return;

        requestApiRunningHistoryList(searchValue, requestHistoriesPage);
    }, [requestHistoriesPage]);

    useEffect(() => {
        if (runnings === undefined) return;
        const array: Array<TypeDTO.RunningHistoryDto> = [];
        runnings.map((running) => {
            running.rtcCheck !== true && array.push(running);
        });
        setFilteredRunnings(array);
    }, [runnings]);

    const onMoreData = () => {
        setLoading(true);
        setRequestHistoriesPage(requestHistoriesPage + 1);
    };

    return (
        <Space direction="vertical" size={24} style={{ display: "flex" }}>
            <Search
                title="기간별 조회"
                values={searchByTime}
                searchBtn
                onSearch={(value) => setDate(value)}
                onClear={(value) => setDate(value)}
                onClick={() => {
                    dispatchRunningsByCar();
                    navigate(Common.PAGE_RUNNING_DETAILS + "/" + carId + "/" + date?.startDate + "/" + date?.endDate);
                }}
            />
            <Space direction="vertical" size={8} style={{ display: "flex" }}>
                <Search
                    title="건별 조회"
                    dataLength={runnings?.length}
                    values={search}
                    onSearch={(value) => onSearchData(value)}
                    onClear={(value) => onSearchData(value)}
                />
                <DataTable
                    rowKey={(row: TypeDTO.RunningHistoryDto) => row.bootTime}
                    disabledTitle
                    loading={loading}
                    columns={columns}
                    dataSource={Utils.roleCheck(userDetails?.user?.authority, [Utils.getAuthority.ROLE_ADMIN]) ? runnings : filteredRunnings}
                    currentPage={currentPage}
                    rowClassName={(history: TypeDTO.RunningHistoryDto) => {
                        return history?.runningHistoryId === reducerRunningsByCar?.selectedId ? "table-row-selected" : "";
                    }}
                    tableSize={reducerRunningsByCar?.pageSize}
                    onRow={(value: TypeDTO.RunningHistoryDto) => {
                        dispatchRunningsByCar(value.runningHistoryId);
                        navigate(Common.PAGE_RUNNING_DETAILS + "/" + value.car.carId + "/" + value.bootTime);
                    }}
                    onChange={onTableChange}
                    isPageable={true}
                    enableMoreData={enableMoreData}
                    onMoreData={onMoreData}
                />
            </Space>
        </Space>
    );
};

export default TabRuninngs;
